import { SoundButton } from "../../common/SoundButton/SoundButton";
import {
    useIsConnectionRestored,
    useTonAddress,
    useTonConnectModal,
    useTonConnectUI,
    useTonWallet,
} from "@tonconnect/ui-react";
import type {
    ApiPersonVerificationChallenge,
    ApiWithdrawalCurrencyCode,
    ApiWithdrawalStatus,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import type { TFunction } from "i18next";
import {
    lazy,
    Suspense,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { SimpleDialog } from "../../common/SimpleDialog/SimpleDialog";
import { SimpleDialogHeader } from "../../common/SimpleDialog/comps/SimpleDialogHeader";
import {
    decimalToMinorUnits,
    formatMinorUnits,
    isFriendlyMainnetTonAddress,
    minorUnitsToDecimal,
} from "../../../logic/helpers/currencyAmount";
import {
    WithdrawalApiError,
    useWithdrawals,
} from "../../../logic/hooks/useWithdrawals";
import type { PersonVerificationWithdrawalResumeOutcome } from "../../../logic/hooks/usePersonVerificationAnalytics";
import "./WithdrawalDialog.scss";

const PersonVerificationFlow = lazy(async () => {
    const module =
        await import("./components/PersonVerificationFlow/PersonVerificationFlow");
    return { default: module.PersonVerificationFlow };
});

const CURRENCIES = ["GRAM", "NOT", "USDT"] as const;
const FALLBACK_SCALES: Record<ApiWithdrawalCurrencyCode, number> = {
    GRAM: 9,
    NOT: 9,
    USDT: 6,
};

const CURRENCY_NAMES: Record<ApiWithdrawalCurrencyCode, string> = {
    GRAM: "GRAM",
    NOT: "Notcoin",
    USDT: "USDt",
};

const OPEN_STATUSES = new Set<ApiWithdrawalStatus>(["REQUESTED", "SUBMITTED"]);

interface WithdrawalNotice {
    amount: string;
    currencyCode: ApiWithdrawalCurrencyCode;
}

interface WithdrawalDialogProps {
    isOpen: boolean;
    initialCurrencyCode?: ApiWithdrawalCurrencyCode;
    onOpen: () => void;
    onClose: () => void;
    onBalancesChanged: () => Promise<void>;
}

function walletDisplayName(appName: string, t: TFunction) {
    if (appName === "telegram-wallet") {
        return t("withdrawal.destination.wallet");
    }
    return appName
        .split(/[-_]/)
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase() + part.slice(1))
        .join(" ");
}

function shortAddress(address: string) {
    return `${address.slice(0, 8)}…${address.slice(-6)}`;
}

function errorMessage(error: unknown, t: TFunction) {
    if (!(error instanceof WithdrawalApiError) || !error.response) {
        return error instanceof Error
            ? error.message
            : t("withdrawal.errors.requestFailed");
    }

    switch (error.response.code) {
        case "INSUFFICIENT_BALANCE":
            return t("withdrawal.errors.insufficientBalance");
        case "WITHDRAWAL_IN_PROGRESS":
            return t("withdrawal.errors.inProgress");
        case "WITHDRAWALS_DISABLED":
        case "CURRENCY_UNAVAILABLE":
        case "PERSON_VERIFICATION_UNAVAILABLE":
            return t("withdrawal.errors.temporarilyUnavailable");
        case "WITHDRAWALS_BLOCKED":
            return t("withdrawal.errors.rejected");
        case "WITHDRAWALS_REVIEW_REQUIRED":
            return t("personVerification.reviewRequired");
        case "INVALID_PAYLOAD":
            return t("withdrawal.errors.invalidPayload");
        case "UNKNOWN":
            return t("withdrawal.errors.deliveryUncertain");
        default:
            return error.response.message ?? t("withdrawal.errors.rejected");
    }
}

function statusLabel(status: ApiWithdrawalStatus, t: TFunction) {
    switch (status) {
        case "REQUESTED":
            return t("withdrawal.statuses.queued");
        case "SUBMITTED":
            return t("withdrawal.statuses.sent");
        case "CONFIRMED":
            return t("withdrawal.statuses.confirmed");
        case "REVERSED":
            return t("withdrawal.statuses.returned");
    }
}

function activeWithdrawalMessage(
    status: ApiWithdrawalStatus,
    currencyCode: ApiWithdrawalCurrencyCode,
    t: TFunction,
) {
    switch (status) {
        case "REQUESTED":
            return t("withdrawal.active.requested", { currencyCode });
        case "SUBMITTED":
            return t("withdrawal.active.submitted", { currencyCode });
        case "CONFIRMED":
            return t("withdrawal.active.confirmed", { currencyCode });
        case "REVERSED":
            return t("withdrawal.active.reversed", { currencyCode });
    }
}

export function WithdrawalDialog({
    isOpen,
    initialCurrencyCode,
    onOpen,
    onClose,
    onBalancesChanged,
}: WithdrawalDialogProps) {
    const { t, i18n } = useTranslation();
    const { state: slotState } = useSlice("slotMachineSlice");
    const withdrawals = useWithdrawals(isOpen);
    const [tonConnectUi] = useTonConnectUI();
    const walletModal = useTonConnectModal();
    const wallet = useTonWallet();
    const connectedAddress = useTonAddress();
    const connectionRestored = useIsConnectionRestored();
    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const [currencyCode, setCurrencyCode] =
        useState<ApiWithdrawalCurrencyCode>("GRAM");
    const [amount, setAmount] = useState("");
    const [shouldDefaultToMax, setShouldDefaultToMax] = useState(true);
    const [manualAddress, setManualAddress] = useState("");
    const [useManualAddress, setUseManualAddress] = useState(false);
    const [submitError, setSubmitError] = useState<unknown>(null);
    const [walletError, setWalletError] = useState<string | null>(null);
    const [notice, setNotice] = useState<WithdrawalNotice | null>(null);
    const [walletSelectorPending, setWalletSelectorPending] = useState(false);
    const [verificationChallenge, setVerificationChallenge] =
        useState<ApiPersonVerificationChallenge | null>(null);
    const restoreAfterWalletModalRef = useRef(false);
    const walletModalOpenedRef = useRef(false);
    const balances = slotState.snapshot?.balances;
    const isBalanceLoading = slotState.loadStatus === "loading";

    const policy = withdrawals.data?.policies.find(
        (entry) => entry.currencyCode === currencyCode,
    );
    const decimalScale = policy?.decimalScale ?? FALLBACK_SCALES[currencyCode];
    const availableMinorUnits =
        balances?.find((balance) => balance.currencyCode === currencyCode)
            ?.balance ?? "0";
    const amountMinorUnits = decimalToMinorUnits(amount, decimalScale, locale);
    const activeWithdrawal = withdrawals.data?.withdrawals.find(
        (entry) =>
            entry.currencyCode === currencyCode &&
            OPEN_STATUSES.has(entry.status),
    );
    const hasPendingWithdrawals = withdrawals.data?.withdrawals.some((entry) =>
        OPEN_STATUSES.has(entry.status),
    );
    const connectedToMainnet = wallet?.account.chain === "-239";
    const destinationAddress = useManualAddress
        ? manualAddress.trim()
        : connectedAddress;
    const destinationIsValid = useManualAddress
        ? isFriendlyMainnetTonAddress(destinationAddress)
        : Boolean(wallet && connectedToMainnet && connectedAddress);
    const shortDateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: "2-digit",
                month: "2-digit",
            }),
        [locale],
    );

    useLayoutEffect(() => {
        if (!isOpen) return;

        if (initialCurrencyCode) setCurrencyCode(initialCurrencyCode);
        setAmount("");
        setShouldDefaultToMax(true);
        setSubmitError(null);
        setNotice(null);
        setVerificationChallenge(null);
    }, [initialCurrencyCode, isOpen]);

    useEffect(() => {
        if (!isOpen || !shouldDefaultToMax || isBalanceLoading || !balances) {
            return;
        }

        setAmount(
            availableMinorUnits === "0"
                ? ""
                : minorUnitsToDecimal(
                      availableMinorUnits,
                      decimalScale,
                      locale,
                  ),
        );
        setShouldDefaultToMax(false);
    }, [
        availableMinorUnits,
        balances,
        decimalScale,
        isBalanceLoading,
        isOpen,
        locale,
        shouldDefaultToMax,
    ]);

    const amountProblem = useMemo(() => {
        if (!amount) return null;
        if (amountMinorUnits === null) {
            return t("withdrawal.validation.decimalPlaces", { decimalScale });
        }
        if (BigInt(amountMinorUnits) <= 0n) {
            return t("withdrawal.validation.greaterThanZero");
        }
        if (policy && BigInt(amountMinorUnits) < BigInt(policy.minimumAmount)) {
            return t("withdrawal.validation.minimum", {
                amount: formatMinorUnits(
                    policy.minimumAmount,
                    decimalScale,
                    locale,
                ),
                currencyCode,
            });
        }
        if (BigInt(amountMinorUnits) > BigInt(availableMinorUnits)) {
            return t("withdrawal.validation.exceedsBalance");
        }
        return null;
    }, [
        amount,
        amountMinorUnits,
        availableMinorUnits,
        currencyCode,
        decimalScale,
        locale,
        policy,
        t,
    ]);

    const canSubmit = Boolean(
        withdrawals.data?.enabled &&
        policy &&
        amountMinorUnits &&
        BigInt(amountMinorUnits) > 0n &&
        !amountProblem &&
        destinationIsValid &&
        !activeWithdrawal &&
        !withdrawals.isSubmitting,
    );

    useEffect(() => {
        if (!isOpen) return;
        void onBalancesChanged();
    }, [isOpen, onBalancesChanged]);

    useEffect(() => {
        if (!isOpen || !hasPendingWithdrawals) return;

        const refresh = async () => {
            await withdrawals.refetch();
            await onBalancesChanged();
        };
        const timer = window.setInterval(() => void refresh(), 15_000);
        return () => window.clearInterval(timer);
    }, [hasPendingWithdrawals, isOpen, onBalancesChanged, withdrawals.refetch]);

    useEffect(() => {
        if (!walletSelectorPending || isOpen) return;

        const frame = window.requestAnimationFrame(() => {
            setWalletSelectorPending(false);
            void tonConnectUi.openModal().catch((error: unknown) => {
                restoreAfterWalletModalRef.current = false;
                walletModalOpenedRef.current = false;
                setWalletError(
                    error instanceof Error
                        ? error.message
                        : t("withdrawal.errors.walletSelector"),
                );
                onOpen();
            });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [isOpen, onOpen, t, tonConnectUi, walletSelectorPending]);

    useEffect(() => {
        if (!restoreAfterWalletModalRef.current) return;
        if (walletModal.state.status === "opened") {
            walletModalOpenedRef.current = true;
            return;
        }
        if (
            walletModal.state.status === "closed" &&
            walletModalOpenedRef.current
        ) {
            restoreAfterWalletModalRef.current = false;
            walletModalOpenedRef.current = false;
            onOpen();
        }
    }, [onOpen, walletModal.state.status]);

    const selectCurrency = (next: ApiWithdrawalCurrencyCode) => {
        setCurrencyCode(next);
        setAmount("");
        setShouldDefaultToMax(true);
        setSubmitError(null);
        setNotice(null);
    };

    const connectWallet = async (replace = false) => {
        setWalletError(null);
        try {
            if (replace && tonConnectUi.connected) {
                await tonConnectUi.disconnect();
            }
            restoreAfterWalletModalRef.current = true;
            walletModalOpenedRef.current = false;
            setWalletSelectorPending(true);
            onClose();
        } catch (error) {
            restoreAfterWalletModalRef.current = false;
            walletModalOpenedRef.current = false;
            setWalletError(
                error instanceof Error
                    ? error.message
                    : t("withdrawal.errors.walletSelector"),
            );
        }
    };

    const submitWithdrawal =
        async (): Promise<PersonVerificationWithdrawalResumeOutcome> => {
            if (!canSubmit || !amountMinorUnits) return "not_submitted";
            setSubmitError(null);
            setNotice(null);
            let withdrawalAccepted = false;

            try {
                const result = await withdrawals.submit({
                    currencyCode,
                    amount: amountMinorUnits,
                    destinationAddress,
                });
                withdrawalAccepted = true;
                setNotice({
                    amount: formatMinorUnits(
                        result.withdrawal.amount,
                        decimalScale,
                        locale,
                    ),
                    currencyCode,
                });
                setAmount("");
                await Promise.all([withdrawals.refetch(), onBalancesChanged()]);
                return "accepted";
            } catch (error) {
                if (withdrawalAccepted) {
                    setSubmitError(error);
                    return "accepted";
                }
                if (
                    error instanceof WithdrawalApiError &&
                    error.response?.code === "PERSON_VERIFICATION_REQUIRED" &&
                    error.response.personVerification
                ) {
                    setVerificationChallenge(error.response.personVerification);
                    return "verification_required";
                }
                setSubmitError(error);
                return "failed";
            }
        };

    const closeDialog = () => {
        setVerificationChallenge(null);
        onClose();
    };

    return (
        <SimpleDialog
            className={[
                "withdrawal-dialog",
                verificationChallenge && "withdrawal-dialog--verification",
            ]
                .filter(Boolean)
                .join(" ")}
            isOpen={isOpen}
            onClose={closeDialog}
            closeOnBackdropClick={!withdrawals.isSubmitting}
            aria-label={
                verificationChallenge
                    ? t("personVerification.header")
                    : undefined
            }
            aria-labelledby={
                verificationChallenge ? undefined : "withdrawal-title"
            }
            header={
                verificationChallenge ? undefined : (
                    <SimpleDialogHeader
                        title={t("withdrawal.title")}
                        titleId="withdrawal-title"
                        closeLabel={t("withdrawal.close")}
                        onClose={closeDialog}
                    />
                )
            }
        >
            {verificationChallenge ? (
                <Suspense
                    fallback={
                        <p className="withdrawal-dialog__verification-loading">
                            {t("personVerification.starting")}
                        </p>
                    }
                >
                    <PersonVerificationFlow
                        challenge={verificationChallenge}
                        currencyCode={currencyCode}
                        onCancel={() => setVerificationChallenge(null)}
                        onVerified={async () => {
                            setVerificationChallenge(null);
                            return submitWithdrawal();
                        }}
                    />
                </Suspense>
            ) : (
                <div className="withdrawal-dialog__body">
                    <p className="withdrawal-dialog__intro">
                        {t("withdrawal.intro")}
                    </p>

                    <div
                        className="withdrawal-dialog__currencies"
                        aria-label={t("withdrawal.assetLabel")}
                    >
                        {CURRENCIES.map((code) => {
                            const entryPolicy = withdrawals.data?.policies.find(
                                (entry) => entry.currencyCode === code,
                            );
                            const scale =
                                entryPolicy?.decimalScale ??
                                FALLBACK_SCALES[code];
                            const balance =
                                balances?.find(
                                    (entry) => entry.currencyCode === code,
                                )?.balance ?? "0";
                            return (
                                <SoundButton
                                    type="button"
                                    key={code}
                                    dir="ltr"
                                    className={
                                        code === currencyCode
                                            ? "is-selected"
                                            : ""
                                    }
                                    onClick={() => selectCurrency(code)}
                                >
                                    <strong>{code}</strong>
                                    <span>
                                        {isBalanceLoading
                                            ? "…"
                                            : formatMinorUnits(
                                                  balance,
                                                  scale,
                                                  locale,
                                              )}
                                    </span>
                                </SoundButton>
                            );
                        })}
                    </div>

                    {Boolean(withdrawals.loadError) && (
                        <p className="withdrawal-dialog__message is-error">
                            {t("withdrawal.errors.loadAvailability")}
                        </p>
                    )}
                    {withdrawals.data && !withdrawals.data.enabled && (
                        <p className="withdrawal-dialog__message is-error">
                            {/* {t("withdrawal.errors.disabled")} */}
                            {t("withdrawal.errors.disabledTemp")}
                        </p>
                    )}

                    <label className="withdrawal-dialog__field">
                        <span>
                            {t("withdrawal.amount.available", {
                                amount: formatMinorUnits(
                                    availableMinorUnits,
                                    decimalScale,
                                    locale,
                                ),
                                currencyCode,
                            })}
                        </span>
                        <div className="withdrawal-dialog__amount-input">
                            <input
                                value={amount}
                                dir="ltr"
                                inputMode="decimal"
                                autoComplete="off"
                                placeholder="0"
                                onChange={(event) => {
                                    setAmount(event.target.value);
                                    setShouldDefaultToMax(false);
                                    setSubmitError(null);
                                    setNotice(null);
                                }}
                            />
                            <SoundButton
                                type="button"
                                onClick={() => {
                                    setAmount(
                                        minorUnitsToDecimal(
                                            availableMinorUnits,
                                            decimalScale,
                                            locale,
                                        ),
                                    );
                                    setShouldDefaultToMax(false);
                                }}
                                disabled={availableMinorUnits === "0"}
                            >
                                {t("withdrawal.amount.max")}
                            </SoundButton>
                        </div>
                        {policy && (
                            <small>
                                {t("withdrawal.amount.minimum", {
                                    amount: formatMinorUnits(
                                        policy.minimumAmount,
                                        decimalScale,
                                        locale,
                                    ),
                                    currencyCode,
                                })}
                            </small>
                        )}
                        {amountProblem && (
                            <small className="is-error">{amountProblem}</small>
                        )}
                    </label>

                    <section className="withdrawal-dialog__destination">
                        <div className="withdrawal-dialog__section-heading">
                            <span>{t("withdrawal.destination.heading")}</span>
                            <SoundButton
                                type="button"
                                onClick={() => {
                                    setUseManualAddress((current) => !current);
                                    setSubmitError(null);
                                    setNotice(null);
                                }}
                            >
                                {useManualAddress
                                    ? t(
                                          "withdrawal.destination.connectedWallet",
                                      )
                                    : t(
                                          "withdrawal.destination.anotherAddress",
                                      )}
                            </SoundButton>
                        </div>

                        {useManualAddress ? (
                            <label className="withdrawal-dialog__field">
                                <input
                                    className="withdrawal-dialog__address-input"
                                    value={manualAddress}
                                    dir="ltr"
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    aria-label={t(
                                        "withdrawal.destination.addressLabel",
                                    )}
                                    placeholder="UQ… or EQ…"
                                    onChange={(event) => {
                                        setManualAddress(event.target.value);
                                        setSubmitError(null);
                                        setNotice(null);
                                    }}
                                />
                                {manualAddress && !destinationIsValid && (
                                    <small className="is-error">
                                        {t("withdrawal.validation.address")}
                                    </small>
                                )}
                            </label>
                        ) : !connectionRestored ? (
                            <div className="withdrawal-dialog__wallet-card">
                                {t("withdrawal.destination.restoring")}
                            </div>
                        ) : wallet && connectedAddress ? (
                            <div className="withdrawal-dialog__wallet-card">
                                <div>
                                    <strong>
                                        {walletDisplayName(
                                            wallet.device.appName,
                                            t,
                                        )}
                                    </strong>
                                    <span title={connectedAddress} dir="ltr">
                                        {shortAddress(connectedAddress)}
                                    </span>
                                </div>
                                <SoundButton
                                    type="button"
                                    onClick={() => void connectWallet(true)}
                                >
                                    {t("withdrawal.destination.change")}
                                </SoundButton>
                            </div>
                        ) : (
                            <SoundButton
                                type="button"
                                className="withdrawal-dialog__connect"
                                onClick={() => void connectWallet()}
                            >
                                {t("withdrawal.destination.connect")}
                            </SoundButton>
                        )}

                        {wallet && !connectedToMainnet && !useManualAddress && (
                            <p className="withdrawal-dialog__message is-error">
                                {t("withdrawal.errors.wrongNetwork")}
                            </p>
                        )}
                        {walletError && (
                            <p className="withdrawal-dialog__message is-error">
                                {walletError}
                            </p>
                        )}
                    </section>

                    {activeWithdrawal && (
                        <p className="withdrawal-dialog__message">
                            {activeWithdrawalMessage(
                                activeWithdrawal.status,
                                currencyCode,
                                t,
                            )}
                        </p>
                    )}
                    {Boolean(submitError) && (
                        <p
                            className="withdrawal-dialog__message is-error"
                            role="alert"
                        >
                            {errorMessage(submitError, t)}
                        </p>
                    )}
                    {notice && (
                        <p
                            className="withdrawal-dialog__message is-success"
                            role="status"
                        >
                            {t("withdrawal.notice.queued", {
                                amount: notice.amount,
                                currencyCode: notice.currencyCode,
                            })}
                        </p>
                    )}

                    <SoundButton
                        type="button"
                        className="withdrawal-dialog__submit"
                        disabled={!canSubmit}
                        onClick={() => void submitWithdrawal()}
                    >
                        {withdrawals.isSubmitting
                            ? t("withdrawal.submit.requesting")
                            : t("withdrawal.submit.action", {
                                  currencyName: CURRENCY_NAMES[currencyCode],
                              })}
                    </SoundButton>

                    {withdrawals.data?.withdrawals.length ? (
                        <section className="withdrawal-dialog__history">
                            <h3>{t("withdrawal.history.recent")}</h3>
                            <ul>
                                {withdrawals.data.withdrawals
                                    .slice(0, 4)
                                    .map((entry) => {
                                        const entryPolicy =
                                            withdrawals.data?.policies.find(
                                                (candidate) =>
                                                    candidate.currencyCode ===
                                                    entry.currencyCode,
                                            );
                                        const scale =
                                            entryPolicy?.decimalScale ??
                                            FALLBACK_SCALES[entry.currencyCode];
                                        return (
                                            <li key={entry.id}>
                                                <span className="withdrawal-dialog__history-transaction">
                                                    <time
                                                        className="withdrawal-dialog__history-date"
                                                        dateTime={
                                                            entry.createdAt
                                                        }
                                                    >
                                                        {shortDateFormatter.format(
                                                            new Date(
                                                                entry.createdAt,
                                                            ),
                                                        )}
                                                    </time>
                                                    <span dir="ltr">
                                                        {formatMinorUnits(
                                                            entry.amount,
                                                            scale,
                                                            locale,
                                                        )}{" "}
                                                        {entry.currencyCode}
                                                    </span>
                                                </span>
                                                <strong
                                                    className={`is-${entry.status.toLowerCase()}`}
                                                >
                                                    {statusLabel(
                                                        entry.status,
                                                        t,
                                                    )}
                                                </strong>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </section>
                    ) : null}
                </div>
            )}
        </SimpleDialog>
    );
}
